import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components/native';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { EmptyStateCard, InfoRow, InfoRowsContainer, } from '../components/AccessibilityInfoComponents';
import AccessibilityCommentSection from '../components/AccessibilityCommentSection';
import { BuildingEntranceSection, BuildingEntranceEmptySection, PlaceEntranceSection, FloorMovementSection, } from '../components/EntranceSection';
import PlaceReviewSection from '../components/PlaceReviewSection';
import { getFloorAccessibility, getAccessibilitySections, } from '../components/PlaceInfo.utils';
/** Compute the chip list for the accessibility tab chip bar. */
export function getAccessibilityChips(accessibility) {
    var _a, _b, _c;
    const placeAccessibilities = (_a = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibilities) !== null && _a !== void 0 ? _a : ((accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility)
        ? [accessibility.placeAccessibility]
        : []);
    if (placeAccessibilities.length === 0)
        return [];
    const pa = placeAccessibilities[0];
    const hasBuildingAccessibility = ((_b = accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibilities) !== null && _b !== void 0 ? _b : []).length > 0 ||
        !!(accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility);
    const isStandalone = pa.isStandaloneBuilding === true;
    const doorDir = pa.doorDirectionType;
    const floors = (_c = pa.floors) !== null && _c !== void 0 ? _c : [];
    const isMultiFloor = floors.length > 1;
    const hasV2Fields = pa.isStandaloneBuilding != null && doorDir != null;
    return getAccessibilitySections({
        isStandalone,
        doorDir,
        isMultiFloor,
        hasV2Fields,
        hasBuildingAccessibility,
        showBuildingEntranceForOutsideDoor: true,
    });
}
export default function V2AccessibilityTab({ accessibility, reviews = [], onRegister, onBuildingRegister, onAddPlaceComment, onAddBuildingComment, onSectionLayout, }) {
    var _a, _b, _c;
    // 다중 출입구 배열 지원 (하위 호환: 단일 필드 fallback)
    const placeAccessibilities = (_a = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibilities) !== null && _a !== void 0 ? _a : ((accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility)
        ? [accessibility.placeAccessibility]
        : []);
    const buildingAccessibilities = (_b = accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibilities) !== null && _b !== void 0 ? _b : ((accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility)
        ? [accessibility.buildingAccessibility]
        : []);
    const hasPlaceAccessibility = placeAccessibilities.length > 0;
    const hasBuildingAccessibility = buildingAccessibilities.length > 0;
    if (!hasPlaceAccessibility) {
        return (_jsx(EmptyStateWrapper, { children: _jsx(EmptyStateCard, { title: '아직 등록된 접근성 정보가 없어요🥲', description: '아래 버튼을 눌러주시면\n최대한 빨리 장소를 정복해볼게요!', buttonText: "\uC815\uBCF4 \uB4F1\uB85D\uD558\uAE30", onPress: onRegister, elementName: "v2_accessibility_tab_empty_register" }) }));
    }
    const primaryPlaceAccessibility = placeAccessibilities[0];
    // V2 건물유형 분기 필드 (첫 번째 PA 기준)
    const isStandalone = primaryPlaceAccessibility.isStandaloneBuilding === true;
    const doorDir = primaryPlaceAccessibility.doorDirectionType;
    const floors = (_c = primaryPlaceAccessibility.floors) !== null && _c !== void 0 ? _c : [];
    const isMultiFloor = floors.length > 1;
    const hasV2Fields = primaryPlaceAccessibility.isStandaloneBuilding != null && doorDir != null;
    // 섹션 순서/가시성을 공유 유틸리티로 결정
    const sections = getAccessibilitySections({
        isStandalone,
        doorDir,
        isMultiFloor,
        hasV2Fields,
        hasBuildingAccessibility,
        showBuildingEntranceForOutsideDoor: true,
    });
    // 층 정보 계산
    const floorInfo = getFloorAccessibility(accessibility, primaryPlaceAccessibility.doorDirectionType);
    const floorDate = dayjs(primaryPlaceAccessibility.createdAt.value).format('YYYY.MM.DD');
    const sectionLayout = (name) => (e) => {
        onSectionLayout === null || onSectionLayout === void 0 ? void 0 : onSectionLayout(name, e.nativeEvent.layout.y);
    };
    /** 매장 출입구 제목 (다중 출입구 시 번호 매김, 1개면 항상 '매장 출입구') */
    const placeEntranceTitle = (baseTitle, index) => placeAccessibilities.length > 1
        ? `${baseTitle} (${index + 1})`
        : '매장 출입구';
    /** 건물 출입구 제목 (다중 출입구 시 번호 매김) */
    const buildingEntranceTitle = (index) => buildingAccessibilities.length > 1
        ? `건물 출입구 (${index + 1})`
        : '건물 출입구';
    return (_jsx(Container, { children: _jsx(SectionsContainer, { children: sections.map(section => {
                switch (section) {
                    case '층 정보':
                        return (_jsx(SectionsInnerContainer, { onLayout: sectionLayout('층 정보'), children: _jsxs(FloorSectionContainer, { children: [_jsxs(FloorSectionHeader, { children: [_jsx(FloorSectionTitle, { children: "\uCE35 \uC815\uBCF4" }), _jsx(FloorSectionDate, { children: floorDate })] }), _jsx(InfoRowsContainer, { children: _jsx(InfoRow, { label: "\uCE35 \uC815\uBCF4", value: floorInfo.title, subValue: floorInfo.description }) })] }) }, section));
                    case '건물 출입구':
                        return (_jsx(SectionsInnerContainer, { onLayout: sectionLayout('건물 출입구'), children: hasBuildingAccessibility ? (buildingAccessibilities.map((ba, index) => {
                                var _a, _b, _c, _d;
                                return (_jsx(BuildingEntranceSection, { buildingDate: dayjs((_b = (_a = ba.createdAt) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : Date.now()).format('YYYY.MM.DD'), buildingAccessibility: ba, title: buildingEntranceTitle(index), children: index === buildingAccessibilities.length - 1 && (_jsx(AccessibilityCommentSection, { comments: (_c = accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibilityComments) !== null && _c !== void 0 ? _c : [], buttonText: "\uAC74\uBB3C\uC785\uAD6C \uC811\uADFC\uC131 \uC815\uBCF4 \uC758\uACAC \uB0A8\uAE30\uAE30", onAddComment: onAddBuildingComment, elementName: "v2_accessibility_building_comment" })) }, (_d = ba.id) !== null && _d !== void 0 ? _d : index));
                            })) : (_jsx(BuildingEntranceEmptySection, { onRegister: onBuildingRegister })) }, section));
                    case '매장 출입구':
                        return (_jsx(SectionsInnerContainer, { onLayout: sectionLayout('매장 출입구'), children: placeAccessibilities.map((pa, index) => {
                                var _a, _b;
                                return (_jsx(PlaceEntranceSection, { title: placeEntranceTitle('매장 출입구', index), placeDate: dayjs(pa.createdAt.value).format('YYYY.MM.DD'), placeAccessibility: pa, children: index === placeAccessibilities.length - 1 && (_jsx(AccessibilityCommentSection, { comments: (_a = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibilityComments) !== null && _a !== void 0 ? _a : [], buttonText: "\uB9E4\uC7A5\uC785\uAD6C \uC811\uADFC\uC131 \uC815\uBCF4 \uC758\uACAC \uB0A8\uAE30\uAE30", onAddComment: onAddPlaceComment, elementName: "v2_accessibility_place_comment", registrationComments: placeAccessibilities
                                            .map(p => p.entranceComment)
                                            .filter((c) => c != null && c.length > 0) })) }, (_b = pa.id) !== null && _b !== void 0 ? _b : index));
                            }) }, section));
                    case '층간 이동 정보':
                        return (_jsx(SectionsInnerContainer, { onLayout: sectionLayout('층간 이동 정보'), children: _jsx(FloorMovementSection, { placeAccessibility: primaryPlaceAccessibility }) }, section));
                    case '내부 이용 정보':
                        return (_jsx(SectionsInnerContainer, { onLayout: sectionLayout('내부 이용 정보'), children: _jsx(PlaceReviewSection, { reviews: reviews, onRegister: onRegister }) }, section));
                    default:
                        return null;
                }
            }) }) }));
}
// ──────────────── 스타일 ────────────────
const Container = styled.View `
  background-color: ${color.white};
`;
// Empty state
const EmptyStateWrapper = styled.View `
  flex: 1;
  background-color: ${color.gray5};
  padding-top: 20px;
`;
// 섹션
const SectionsContainer = styled.View `
  padding: 20px;
  gap: 40px;
`;
const SectionsInnerContainer = styled.View `
  gap: 40px;
`;
// 층 정보 섹션 (로컬 - EntranceSection과 다른 구조)
const FloorSectionContainer = styled.View `
  gap: 16px;
`;
const FloorSectionHeader = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const FloorSectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  color: ${color.gray90};
`;
const FloorSectionDate = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.gray50};
`;
